import { useEdge } from '@/components/Edge';
import { StandaloneArticle } from '@/components/post-type/Article';
import { fetchNativeArticle, updateAllerGAData } from '@/components/SecondNativeArticle/helpers';
import { LabradorComponent, StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { Content, Data, Meta } from '@/types/content';
import { SecondNativeArticleCampaign } from '@/types/secondNativeArticleCampaign';
import { useAppState } from 'lib/hooks/useAppState';
import { useIntersectionObserver } from 'lib/hooks/useIntersectionObserver';
import useLazyCallback from 'lib/hooks/useLazyCallback';
import { useSeenArticleCampaignsLocalStorage } from 'lib/hooks/useSeenArticleCampaignsLocalStorage';
import getConfig from 'modules/config';
import { standaloneComponent } from 'modules/dynamic-components';
import { useRouter } from 'next/router';
import { useCallback, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { isNotTrue, isNull, isUndefined } from 'typesafe-utils';

const Article = standaloneComponent('PostTypeArticle') as typeof StandaloneArticle;

const registerPageView = (allerGAData: Record<string, string>) => {
  updateAllerGAData(allerGAData);
  window.cts?.send?.pageview?.();
};

export interface StandaloneSecondNativeArticle extends StandaloneComponentProps {
  meta: Meta;
  data: Data;
  descendants: Content[];
  campaign: SecondNativeArticleCampaign;
}
export const StandaloneSecondNativeArticle: StandaloneComponent<StandaloneSecondNativeArticle> = ({
  meta,
  data,
  descendants,
  campaign,
}) => {
  const edgeRef = useEdge();
  const [isCampaignRegistered, setIsCampaignRegistered] = useState(false);
  const [_, registerCampaignView] = useSeenArticleCampaignsLocalStorage();
  const [{ publishedUrl }] = useAppState();
  const articleRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const { analyticsData } = data ?? {};
  const { allerGaData } = analyticsData ?? {};

  const getSecondNativeUrl = () => {
    const searchParams = new URLSearchParams({
      second_article: String(true),
    }).toString();

    return data.publishedUrl + '?' + searchParams;
  };

  const callback: IntersectionObserverCallback = useCallback(
    (entries) => {
      entries.forEach(async (entry) => {
        const isArticleVisible = entry.isIntersecting;
        const isArticleFullyRead = entry.boundingClientRect.y < 0;
        const isArticleActive = isArticleVisible || isArticleFullyRead;
        const isPlayerFloating = window.jwplayer?.()?.getFloating();

        const resolvedUrl = isArticleActive ? getSecondNativeUrl() : publishedUrl;

        if (isArticleVisible && isPlayerFloating) {
          window.jwplayer?.()?.setFloating(false);
        }

        if (resolvedUrl && router.asPath !== resolvedUrl) {
          await router.push(resolvedUrl, undefined, { shallow: true, scroll: false });
        }

        if (isArticleActive && !isCampaignRegistered) {
          setIsCampaignRegistered(true);
          registerCampaignView(campaign.campaignId);
          registerPageView(allerGaData);
        }
      });
    },
    [publishedUrl, router, campaign.campaignId, registerCampaignView, allerGaData, isCampaignRegistered]
  );

  useIntersectionObserver([articleRef], callback, {
    rootMargin: `0px 0px -${window.innerHeight}px 0px`,
  });

  const nativeAdLabel = meta?.nativeAdLabel || '';
  const affiliateLabel = data?.affiliateLabel || '';

  const disclosureLabels = [];

  if (meta?.isNativeAd && nativeAdLabel) disclosureLabels.push(nativeAdLabel);
  if (data?.hasAffiliate && affiliateLabel) disclosureLabels.push(affiliateLabel);

  return (
    <div ref={articleRef}>
      <Article
        articleDisclosureLabels={disclosureLabels}
        descendants={descendants}
        options={{ 'data-cts-scroll-depth-item': 'second-native-article' }}
        hide={{
          trendingMenu: true,
          breakingNews: true,
          recommendedArticles: true,
          badge: true,
        }}
      />
    </div>
  );
};

export const standalone = StandaloneSecondNativeArticle;

const DynamicStandaloneSecondNativeArticle = standaloneComponent('ElementSecondNativeArticle') as typeof standalone;

const LabradorSecondNativeArticle: LabradorComponent = () => {
  const [{ device, secondNativeArticleCampaigns = [], pageId }] = useAppState();
  const [seenCampaignsLocalStorage] = useSeenArticleCampaignsLocalStorage();
  const [secondNativeArticle, setSecondNativeArticle] = useState<Content | null | undefined>(undefined);
  const [campaign, setCampaign] = useState<SecondNativeArticleCampaign | undefined>(undefined);

  useEffectOnce(() => {
    const notSeenCampaign = secondNativeArticleCampaigns.find(
      (campaign) =>
        campaign.articleId !== String(pageId) &&
        !seenCampaignsLocalStorage.some(
          ({ campaignId, userFrequency }) =>
            campaignId === campaign.campaignId && (userFrequency ?? 1) >= (campaign.userFrequency ?? 1)
        )
    );

    setCampaign(notSeenCampaign);
  });

  const getNativeArticle = useCallback(async () => {
    const data = await fetchNativeArticle(campaign?.articleId || '', device);

    // make sure the second native article is the same brand as the current article
    const isNativeArticleFromDifferentBrand = data?.type === '308';
    if (!isNativeArticleFromDifferentBrand) {
      setSecondNativeArticle(data);
    }
  }, [campaign?.articleId, device]);

  const ref = useLazyCallback<HTMLDivElement>(getNativeArticle, {
    rootMargin: '3000px',
  });

  const isDisabled = isNotTrue(getConfig('secondNativeArticle.enabled'));
  const isCampaignMissing = !campaign;

  if (isDisabled || isCampaignMissing || isNull(secondNativeArticle)) {
    return null;
  }

  if (isUndefined(secondNativeArticle)) {
    return <div ref={ref} />;
  }

  return (
    <DynamicStandaloneSecondNativeArticle
      data={secondNativeArticle.data}
      descendants={secondNativeArticle.children}
      meta={secondNativeArticle.meta}
      campaign={campaign}
    />
  );
};
export default LabradorSecondNativeArticle;
